import * as React from 'react';
import {
    Alert, Box, Button, Card, CardActionArea, CardContent, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, FormControlLabel, IconButton, MenuItem, Stack, Switch, Table, TableBody, TableCell, TableHead,
    TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AppleIcon from '@mui/icons-material/Apple';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import BlockIcon from '@mui/icons-material/Block';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import RuleIcon from '@mui/icons-material/Rule';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import RedeemIcon from '@mui/icons-material/Redeem';
import ReplayIcon from '@mui/icons-material/Replay';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import ShopIcon from '@mui/icons-material/Shop';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import {
    applyUserSubscriptionBenefits, giveUserSubscriptionOffer, grantUserSubscription, revokeUserSubscription,
    revokeUserSubscriptionOffer, updateUserSubscriptionEffect, validateUserSubscription,
} from '../Api';
import SubscriptionTierChip from './SubscriptionTierChip';
import UserPremiumAdminDto, {
    EffectOptionDto, OfferBenefitDto, SubscriptionAdminDto, SubscriptionBenefitDto, SubscriptionEffectDto, SubscriptionEventDto,
    SubscriptionOfferOptionDto, SubscriptionPlanAdminDto, SubscriptionValidationDto, UserOfferStatus, UserSubscriptionOfferDto,
    ValidationLevel,
} from '../models/dtos/UserPremiumAdminDto';

type PremiumResult = { data: UserPremiumAdminDto | null; error: string | null };

const TIER_LABEL: Record<string, string> = { plus: 'BETUP Plus', max: 'BETUP Max' };

function formatDate(value: string | number | null): string {
    return value == null ? '—' : new Date(value).toLocaleString();
}

function formatDay(value: string | number | null): string {
    return value == null ? '—' : new Date(value).toLocaleDateString();
}

function formatPrice(price: number | null, currency: string | null): string {
    if (price == null) return '';
    return `${currency === 'USD' || currency == null ? '$' : currency + ' '}${Number(price).toFixed(2)}`;
}

/** `datetime-local` value in the browser's zone. */
function toLocalInput(value: string | number | null): string {
    if (value == null) return '';
    const d = new Date(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** TRANSFERRED (moved to the account that restored it) is ended like EXPIRED, but nothing went wrong: info, not error. */
function statusColor(status: string): 'success' | 'warning' | 'default' | 'error' | 'info' {
    return status === 'ACTIVE' ? 'success' : status === 'IN_GRACE' ? 'warning' : status === 'REVOKED' ? 'error'
        : status === 'TRANSFERRED' ? 'info' : 'default';
}

const LEVEL_COLOR: Record<ValidationLevel, 'success' | 'warning' | 'error'> = { OK: 'success', WARN: 'warning', ERROR: 'error' };

/** Subscription effect groups (betup-server-api docs/subscriptions.md §2). */
export const GROUP_INFO: Record<number, { label: string; icon: React.ReactElement }> = {
    701: { label: 'Ace access', icon: <AutoAwesomeIcon fontSize="small" /> },
    702: { label: 'No ads', icon: <BlockIcon fontSize="small" /> },
    703: { label: 'Win booster', icon: <TrendingUpIcon fontSize="small" /> },
    704: { label: 'Loss cashback', icon: <ReplayIcon fontSize="small" /> },
    705: { label: 'Shop coin bonus', icon: <SavingsOutlinedIcon fontSize="small" /> },
};

export function groupIcon(group: number | undefined): React.ReactElement {
    return (group != null ? GROUP_INFO[group]?.icon : undefined) ?? <StarOutlineIcon fontSize="small" />;
}

/** A plan benefit's icon and wording; the effect group comes from the effect catalogue. */
export function benefitView(b: SubscriptionBenefitDto, options: EffectOptionDto[]): { icon: React.ReactElement; label: string; detail: string } {
    if (b.type === 'DAILY_COINS') {
        return { icon: <RedeemIcon fontSize="small" />, label: `${(b.dailyCoins ?? 0).toLocaleString()} betcoins a day`, detail: 'Subscription daily bonus, claimed on Home' };
    }
    const option = options.find(o => o.id === b.effectId);
    const group = option?.group;
    return {
        icon: groupIcon(group),
        label: b.effectName ?? `Effect ${b.effectId}`,
        detail: [group != null ? GROUP_INFO[group]?.label ?? `group ${group}` : null, `effect #${b.effectId}`, option ? `coef ${option.coef}` : null]
            .filter(Boolean).join(' · '),
    };
}

export const EVENT_ICON: Record<string, React.ReactElement> = {
    PURCHASED: <ShoppingCartOutlinedIcon fontSize="small" />,
    RENEWED: <AutorenewIcon fontSize="small" />,
    GRANTED: <CardGiftcardIcon fontSize="small" />,
    UPGRADED: <UpgradeIcon fontSize="small" />,
    GRACE_STARTED: <HourglassBottomIcon fontSize="small" />,
    CANCELED: <CancelOutlinedIcon fontSize="small" />,
    EXPIRED: <EventRepeatIcon fontSize="small" />,
    REVOKED: <BlockIcon fontSize="small" />,
    TRANSFERRED_OUT: <CallMadeIcon fontSize="small" />,
    TRANSFERRED_IN: <CallReceivedIcon fontSize="small" />,
    OFFER_TAKEN: <LocalOfferIcon fontSize="small" />,
    OFFER_REVOKED: <DoNotDisturbOnOutlinedIcon fontSize="small" />,
};

export const OFFER_STATUS_COLOR: Record<UserOfferStatus, 'info' | 'success' | 'default' | 'error'> = {
    AVAILABLE: 'info', TAKEN: 'success', EXPIRED: 'default', REVOKED: 'error',
};

/** A benefit offer's boosts grouped by plan, in the plans' order (unknown plans last). */
export function boostsByPlan(benefits: OfferBenefitDto[], plans: SubscriptionPlanAdminDto[]): { plan: string; name: string; boosts: OfferBenefitDto[] }[] {
    const codes = Array.from(new Set(benefits.map(b => b.plan ?? '?')));
    const rank = (code: string) => {
        const i = plans.findIndex(p => p.code === code);
        return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };
    return codes.sort((a, b) => rank(a) - rank(b)).map(code => ({
        plan: code,
        name: plans.find(p => p.code === code)?.name ?? code,
        boosts: benefits.filter(b => (b.plan ?? '?') === code),
    }));
}

/** A boost's short wording: the effect's name, or the betcoins a day. */
export function boostLabel(b: OfferBenefitDto): string {
    return b.type === 'DAILY_COINS' ? `${(b.dailyCoins ?? 0).toLocaleString()} / day` : b.effectName ?? `Effect ${b.effectId}`;
}

/** Compact per-plan boosts of a benefit offer: a line per plan (its code, then a chip per boost; details on hover). */
export function OfferBoosts({ benefits, plans, options }: {
    benefits: OfferBenefitDto[];
    plans: SubscriptionPlanAdminDto[];
    options: EffectOptionDto[];
}) {
    if (benefits.length === 0) return <Typography variant="caption" color="text.secondary">No boosts</Typography>;
    return (
        <Stack gap={0.5}>
            {boostsByPlan(benefits, plans).map(group => (
                <Stack key={group.plan} direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
                    <Tooltip title={group.name}>
                        <Chip size="small" label={group.plan} sx={{ fontFamily: 'monospace', minWidth: 44 }}
                            color={group.plan === 'max' ? 'warning' : 'primary'} />
                    </Tooltip>
                    {group.boosts.map((b, i) => {
                        const view = benefitView(b, options);
                        return (
                            <Tooltip key={i} title={`${view.label} · ${view.detail}`}>
                                <Chip size="small" variant="outlined" icon={view.icon} label={boostLabel(b)} />
                            </Tooltip>
                        );
                    })}
                </Stack>
            ))}
        </Stack>
    );
}

export function storeIcon(shop: string): React.ReactElement {
    if (shop === 'BETUP') return <CardGiftcardOutlinedIcon fontSize="small" />;
    if (shop.toLowerCase().includes('apple') || shop.toLowerCase() === 'ios') return <AppleIcon fontSize="small" />;
    return <ShopIcon fontSize="small" />;
}

/** A titled block: an icon, a title, optional actions on the right, then its content. */
function Block({ icon, title, actions, children }: {
    icon: React.ReactElement;
    title: string;
    actions?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
                    <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {actions}
                </Stack>
                {children}
            </CardContent>
        </Card>
    );
}

function Empty({ text }: { text: string }) {
    return <Typography variant="body2" color="text.secondary">{text}</Typography>;
}

/** A small count tile with its latest rows. */
function ActivityTile({ icon, title, count, lines, empty }: {
    icon: React.ReactElement;
    title: string;
    count: string;
    lines: string[];
    empty: string;
}) {
    return (
        <Card variant="outlined" sx={{ flex: '1 1 220px' }}>
            <CardContent>
                <Stack direction="row" gap={1} alignItems="center">
                    <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
                    <Typography variant="subtitle2">{title}</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="h6">{count}</Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                {lines.length === 0
                    ? <Empty text={empty} />
                    : lines.map((line, i) => <Typography key={i} variant="caption" component="div" color="text.secondary">{line}</Typography>)}
            </CardContent>
        </Card>
    );
}

/**
 * The Premium tab's subscription card (analytics PremiumAdminService, betup-server-api docs/subscriptions.md §11), in
 * blocks: status and actions, the plans (click one for its details), the player's subscriptions with their events
 * (validate, apply benefits, revoke), BETUP grants, the effects of the subscription groups (edit: row, end, on / off),
 * and activity — daily bonuses claimed, allowance resets, predictions unlocked for a video.
 */
export default function UserSubscriptionsCard({ userId, premium, loading, error, run }: {
    userId: number;
    premium: UserPremiumAdminDto | null;
    loading: boolean;
    error: string | null;
    /** Runs a call returning the new state; `done` is the toast. Resolves true on success. */
    run: (action: () => Promise<PremiumResult>, done: string) => Promise<boolean>;
}) {
    const [grantOpen, setGrantOpen] = React.useState(false);
    const [revoking, setRevoking] = React.useState<SubscriptionAdminDto | null>(null);
    const [giveOpen, setGiveOpen] = React.useState(false);
    const [offerRevoking, setOfferRevoking] = React.useState<UserSubscriptionOfferDto | null>(null);
    const [editing, setEditing] = React.useState<SubscriptionEffectDto | null>(null);
    const [planShown, setPlanShown] = React.useState<SubscriptionPlanAdminDto | null>(null);
    const [validation, setValidation] = React.useState<SubscriptionValidationDto | null>(null);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const [validating, setValidating] = React.useState<number | null>(null);

    const validate = async (subscriptionId: number) => {
        setValidating(subscriptionId);
        const result = await validateUserSubscription(userId, subscriptionId);
        setValidating(null);
        setValidation(result.data);
        setValidationError(result.error);
        if (result.data == null && result.error == null) setValidationError('Validation failed');
    };

    const applyBenefits = async (subscriptionId: number) => {
        if (await run(() => applyUserSubscriptionBenefits(userId, subscriptionId), 'Benefits re-applied')) validate(subscriptionId);
    };

    const access = premium?.aceAccess;
    const live = premium?.subscriptions.find(s => s.live) ?? null;
    const hasStore = premium?.subscriptions.some(s => s.live && s.shop !== 'BETUP') ?? false;
    const planByCode = (code: string | null) => premium?.plans.find(p => p.code === code) ?? null;
    const claims = premium?.dailyClaims ?? [];
    const options = premium?.effectOptions ?? [];

    return (
        <Stack gap={2}>
            {/* Status and actions */}
            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                        <WorkspacePremiumIcon color="primary" />
                        <Typography variant="h6">Subscription</Typography>
                        {premium && <SubscriptionTierChip tier={premium.tier} showFree size="medium" />}
                        {live
                            ? <Chip color="success" icon={<WorkspacePremiumIcon />} label={`${live.planName ?? live.product} · until ${formatDay(live.graceUntil)}`} />
                            : <Chip variant="outlined" label="No live subscription" />}
                        {access && (access.active
                            ? <Chip color="primary" variant="outlined" icon={<AutoAwesomeIcon />}
                                label={`Ace ${TIER_LABEL[access.tier ?? 'plus']}${access.until ? ` · ${formatDay(access.until)}` : ''}`} />
                            : <Chip variant="outlined" icon={<AutoAwesomeIcon />} label="No Ace" />)}
                        <Tooltip title={'A subscription\'s benefits are effects tagged with it, lasting until its grace end. The dashboard grants BETUP '
                            + 'subscriptions only (nothing charged); revoke a live store subscription before granting.'}>
                            <InfoOutlinedIcon fontSize="small" color="action" />
                        </Tooltip>
                        <Box sx={{ flexGrow: 1 }} />
                        <Tooltip title={hasStore ? 'Revoke the live store subscription first' : ''}>
                            <span>
                                <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => setGrantOpen(true)}
                                    disabled={loading || premium == null || hasStore}>
                                    Grant BETUP
                                </Button>
                            </span>
                        </Tooltip>
                    </Stack>
                    {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
                    {loading && premium == null && <CircularProgress size={24} sx={{ mt: 1.5 }} />}
                </CardContent>
            </Card>

            {premium && (
                <>
                    {/* Plans */}
                    <Block icon={<PaymentsOutlinedIcon />} title="Plans">
                        <Stack direction="row" gap={1.5} flexWrap="wrap">
                            {premium.plans.map(plan => {
                                const product = plan.products[0];
                                return (
                                    <Card key={plan.id} variant="outlined" sx={{ flex: '1 1 240px', opacity: plan.active ? 1 : 0.6 }}>
                                        <CardActionArea onClick={() => setPlanShown(plan)} sx={{ p: 1.5 }}>
                                            <Stack direction="row" gap={1} alignItems="center">
                                                <WorkspacePremiumIcon color={plan.code === 'max' ? 'warning' : 'primary'} />
                                                <Typography fontWeight={700}>{plan.name}</Typography>
                                                <SubscriptionTierChip tier={plan.tier} />
                                                {!plan.active && <Chip size="small" label="inactive" />}
                                                <Box sx={{ flexGrow: 1 }} />
                                                <Typography fontWeight={700} color="primary.main">
                                                    {product ? `${formatPrice(product.price, product.currency)} / ${product.periodDays} d` : 'no product'}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" gap={0.75} sx={{ mt: 1, color: 'text.secondary' }}>
                                                {plan.benefits.map((b, i) => {
                                                    const view = benefitView(b, options);
                                                    return <Tooltip key={i} title={view.label}>{view.icon}</Tooltip>;
                                                })}
                                            </Stack>
                                        </CardActionArea>
                                    </Card>
                                );
                            })}
                        </Stack>
                    </Block>

                    {/* Subscriptions */}
                    <Block icon={<ShoppingCartOutlinedIcon />} title="Subscriptions">
                        {premium.subscriptions.length === 0
                            ? <Empty text="No subscriptions." />
                            : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Plan</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Store</TableCell>
                                            <TableCell>Period</TableCell>
                                            <TableCell>Benefits until</TableCell>
                                            <TableCell>Daily betcoins</TableCell>
                                            <TableCell>History</TableCell>
                                            <TableCell align="right" />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {premium.subscriptions.map(sub => {
                                            const plan = planByCode(sub.plan);
                                            return (
                                                <TableRow key={sub.id}>
                                                    <TableCell>
                                                        <Button size="small" startIcon={<WorkspacePremiumIcon />} disabled={plan == null}
                                                            onClick={() => plan && setPlanShown(plan)} sx={{ textTransform: 'none' }}>
                                                            {sub.planName ?? sub.product}
                                                        </Button>
                                                        <Typography variant="caption" color="text.secondary">#{sub.id}</Typography>
                                                        {sub.userOfferId != null && (
                                                            <Tooltip title={`Holds benefit offer #${sub.userOfferId}`}>
                                                                <Chip size="small" color="secondary" variant="outlined" icon={<LocalOfferOutlinedIcon />}
                                                                    label={`Promo: ${sub.offerName ?? `#${sub.userOfferId}`}`} sx={{ ml: 1 }} />
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell><Chip size="small" color={statusColor(sub.status)} label={sub.status} /></TableCell>
                                                    <TableCell>
                                                        <Chip size="small" variant="outlined" icon={storeIcon(sub.shop)} label={sub.shop + (sub.test ? ' · test' : '')} />
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDay(sub.periodStart)} → {formatDay(sub.periodEnd)}</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(sub.graceUntil)}</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        <Stack direction="row" gap={0.5} alignItems="center">
                                                            <Typography variant="body2" fontWeight={600}>{sub.dailyCoins.toLocaleString()}</Typography>
                                                            {sub.dailyCoins > sub.planDailyCoins && (
                                                                <Tooltip title={`From its benefit offer; the plan gives ${sub.planDailyCoins.toLocaleString()}`}>
                                                                    <Chip size="small" color="secondary" variant="outlined" label="promo" />
                                                                </Tooltip>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                                                            {sub.events.map((e, i) => <EventChip key={i} event={e} />)}
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                        <Tooltip title="Validate">
                                                            <span>
                                                                <IconButton size="small" onClick={() => validate(sub.id)} disabled={validating != null}>
                                                                    {validating === sub.id ? <CircularProgress size={16} /> : <FactCheckOutlinedIcon fontSize="small" />}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        {sub.live && (
                                                            <Tooltip title="Revoke">
                                                                <IconButton size="small" color="error" onClick={() => setRevoking(sub)} disabled={loading}>
                                                                    <BlockIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                    </Block>

                    {/* Benefit offers */}
                    <Block icon={<LocalOfferOutlinedIcon />} title="Subscription offers" actions={(
                        <Tooltip title={premium.offerTemplates.length === 0 ? 'No active offer template (Marketing › Subscriptions › Offers)' : ''}>
                            <span>
                                <Button size="small" variant="outlined" startIcon={<LocalOfferOutlinedIcon />} onClick={() => setGiveOpen(true)}
                                    disabled={loading || premium.offerTemplates.length === 0}>
                                    Give offer
                                </Button>
                            </span>
                        </Tooltip>
                    )}>
                        {premium.offers.length === 0
                            ? <Empty text="No offers given." />
                            : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Offer</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Accepted</TableCell>
                                            <TableCell>Expires</TableCell>
                                            <TableCell>Taken</TableCell>
                                            <TableCell>Subscription</TableCell>
                                            <TableCell>Source</TableCell>
                                            <TableCell>Boosts</TableCell>
                                            <TableCell>Note</TableCell>
                                            <TableCell align="right" />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {premium.offers.map(o => (
                                            <TableRow key={o.id}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>{o.offerName ?? `Template #${o.offerId}`}</Typography>
                                                    <Typography variant="caption" color="text.secondary">#{o.id} · given {formatDay(o.createdAt)}</Typography>
                                                </TableCell>
                                                <TableCell><Chip size="small" color={OFFER_STATUS_COLOR[o.status] ?? 'default'} label={o.status} /></TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                    {o.accepted
                                                        ? (
                                                            <Stack direction="row" gap={0.5} alignItems="center">
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                                {o.acceptedPlan && <Chip size="small" label={o.acceptedPlan} sx={{ fontFamily: 'monospace' }} />}
                                                            </Stack>
                                                        )
                                                        : <Typography variant="body2" color="text.secondary">—</Typography>}
                                                </TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(o.expiresAt)}</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(o.takenAt)}</TableCell>
                                                <TableCell>{o.subscriptionId != null ? `#${o.subscriptionId}` : '—'}</TableCell>
                                                <TableCell>
                                                    {o.source === 'RULE'
                                                        ? (
                                                            <Tooltip title={o.sourceRef ? `Rule ${o.sourceRef}` : 'Rule'}>
                                                                <Chip size="small" variant="outlined" icon={<RuleIcon />}
                                                                    label={o.sourceRef ? `RULE · ${o.sourceRef.slice(-6)}` : 'RULE'} />
                                                            </Tooltip>
                                                        )
                                                        : <Chip size="small" variant="outlined" icon={<AdminPanelSettingsIcon />} label="DASHBOARD" />}
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 280 }}><OfferBoosts benefits={o.benefits} plans={premium.plans} options={options} /></TableCell>
                                                <TableCell sx={{ maxWidth: 200 }}>
                                                    <Typography variant="caption" color="text.secondary">{o.note ?? ''}</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {(o.status === 'AVAILABLE' || o.status === 'TAKEN') && (
                                                        <Tooltip title="Revoke offer">
                                                            <IconButton size="small" color="error" onClick={() => setOfferRevoking(o)} disabled={loading}>
                                                                <BlockIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                    </Block>

                    {/* Effects */}
                    <Block icon={<AutoAwesomeIcon />} title="Subscription effects">
                        {premium.effects.length === 0
                            ? <Empty text="None." />
                            : (
                                <Stack divider={<Divider flexItem />}>
                                    {premium.effects.map(e => (
                                        <Stack key={e.userEffectId} direction="row" gap={1.5} alignItems="center" sx={{ py: 0.75, opacity: e.live ? 1 : 0.55 }}>
                                            <Box sx={{ color: e.live ? 'primary.main' : 'text.disabled', display: 'flex' }}>{groupIcon(e.group)}</Box>
                                            <Box sx={{ minWidth: 180 }}>
                                                <Typography variant="body2" fontWeight={600}>{e.effectName ?? `Effect ${e.effectId}`}</Typography>
                                                <Typography variant="caption" color="text.secondary">#{e.userEffectId} · {GROUP_INFO[e.group]?.label ?? `group ${e.group}`}</Typography>
                                            </Box>
                                            {e.sourceType === 'SUBSCRIPTION'
                                                ? <Chip size="small" variant="outlined" icon={<LinkIcon />} label={`Sub #${e.sourceId}`} />
                                                : <Chip size="small" variant="outlined" icon={<AdminPanelSettingsIcon />} label={e.causeLabel} />}
                                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                                {formatDay(e.applied)} → {e.expires == null ? 'no end' : formatDate(e.expires)}
                                            </Typography>
                                            <Box sx={{ flexGrow: 1 }} />
                                            {e.live ? <Chip size="small" color="success" label="live" />
                                                : <Chip size="small" variant="outlined" label={e.isActive ? 'expired' : 'off'} />}
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => setEditing(e)} disabled={loading}><EditOutlinedIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                    </Block>

                    {/* Activity */}
                    <Stack direction="row" gap={2} flexWrap="wrap">
                        <ActivityTile
                            icon={<RedeemIcon />}
                            title="Daily bonuses"
                            count={claims.reduce((sum, c) => sum + c.amount, 0).toLocaleString()}
                            lines={claims.slice(0, 5).map(c => `${formatDay(c.claimDay)} · ${c.amount.toLocaleString()} · sub #${c.subscriptionId}`)}
                            empty="None claimed."
                        />
                        <ActivityTile
                            icon={<RestartAltIcon />}
                            title="Allowance resets"
                            count={String(premium.resets.length)}
                            lines={premium.resets.slice(0, 5).map(r => `${formatDate(r.createdAt)} · ${r.store}${r.test ? ' (test)' : ''}`)}
                            empty="None."
                        />
                        <ActivityTile
                            icon={<OndemandVideoIcon />}
                            title="Video unlocks"
                            count={String(premium.unlocks.length)}
                            lines={premium.unlocks.slice(0, 5).map(u => `${formatDate(u.createdAt)} · match #${u.matchId}`)}
                            empty="None."
                        />
                    </Stack>
                </>
            )}

            <PlanDialog plan={planShown} options={options} onClose={() => setPlanShown(null)} />
            {premium && (
                <GrantDialog
                    open={grantOpen}
                    plans={premium.plans.filter(p => p.active)}
                    onClose={() => setGrantOpen(false)}
                    onGrant={async (plan, days, note) => {
                        if (await run(() => grantUserSubscription(userId, plan, days, note), `${TIER_LABEL[plan] ?? plan} granted (BETUP subscription)`)) setGrantOpen(false);
                    }}
                />
            )}
            {premium && (
                <GiveOfferDialog
                    open={giveOpen}
                    templates={premium.offerTemplates}
                    plans={premium.plans}
                    options={options}
                    error={error}
                    loading={loading}
                    onClose={() => setGiveOpen(false)}
                    onGive={async (template, days, note, push) => {
                        if (await run(() => giveUserSubscriptionOffer(userId, template.id, days, note || undefined, push),
                            `Offer “${template.name}” given for ${days} days`)) setGiveOpen(false);
                    }}
                />
            )}
            <RevokeOfferDialog
                offer={offerRevoking}
                error={error}
                loading={loading}
                onClose={() => setOfferRevoking(null)}
                onRevoke={async note => {
                    const o = offerRevoking;
                    if (o && await run(() => revokeUserSubscriptionOffer(userId, o.id, note || undefined), `Offer #${o.id} revoked`)) setOfferRevoking(null);
                }}
            />
            <RevokeDialog
                subscription={revoking}
                onClose={() => setRevoking(null)}
                onRevoke={async note => {
                    const sub = revoking;
                    if (sub && await run(() => revokeUserSubscription(userId, sub.id, note), `Subscription #${sub.id} revoked`)) setRevoking(null);
                }}
            />
            <EditEffectDialog
                effect={editing}
                options={options.filter(o => o.group === editing?.group)}
                onClose={() => setEditing(null)}
                onSave={async change => {
                    const e = editing;
                    if (e && await run(() => updateUserSubscriptionEffect(userId, e.userEffectId, change), `Effect #${e.userEffectId} updated`)) setEditing(null);
                }}
            />
            <Dialog open={validation != null || validationError != null} onClose={() => { setValidation(null); setValidationError(null); }}
                maxWidth="md" fullWidth>
                <DialogTitle>
                    {validation ? `Subscription #${validation.subscriptionId} · ${validation.status} · ${validation.valid ? 'valid' : 'invalid'}` : 'Validation'}
                </DialogTitle>
                <DialogContent>
                    {validationError && <Alert severity="error">{validationError}</Alert>}
                    {validation && (
                        <Stack gap={1}>
                            {validation.checks.map((c, i) => (
                                <Stack key={i} direction="row" gap={1} alignItems="center">
                                    <Chip size="small" color={LEVEL_COLOR[c.level]} label={c.level} sx={{ minWidth: 64 }} />
                                    <Typography variant="caption" sx={{ minWidth: 72, fontFamily: 'monospace' }}>{c.code}</Typography>
                                    <Typography variant="body2">{c.message}</Typography>
                                </Stack>
                            ))}
                            {validation.fixable && (
                                <Alert severity="info">
                                    Apply benefits makes the subscription's effects what it should have given: its plan's benefits until its
                                    grace end while it lasts (missing ones created, others ended), none once it has ended.
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    {validation?.fixable && (
                        <Button color="warning" variant="contained" disabled={loading}
                            onClick={() => applyBenefits(validation.subscriptionId)}>
                            Apply benefits
                        </Button>
                    )}
                    <Button onClick={() => { setValidation(null); setValidationError(null); }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

const TRANSFER_EVENTS = ['TRANSFERRED_OUT', 'TRANSFERRED_IN'];

/**
 * One subscription event: its icon and type; the date, price and note on hover. A move between accounts says where to
 * ("Moved to user #8") with that account's subscription row, and opens that user's Premium tab in a new tab (the user
 * page doesn't reload on a new id in place).
 */
function EventChip({ event }: { event: SubscriptionEventDto }) {
    const details = [formatDate(event.createdAt), event.price != null ? formatPrice(event.price, event.currency) : null,
        event.periodEnd != null ? `period end ${formatDay(event.periodEnd)}` : null, event.note,
        event.purchaseId != null ? `purchase #${event.purchaseId}` : null].filter(Boolean).join(' · ');
    if (TRANSFER_EVENTS.includes(event.type) && event.relatedUserId != null) {
        const out = event.type === 'TRANSFERRED_OUT';
        const who = `user #${event.relatedUserId}`;
        const row = event.relatedSubscriptionId != null ? ` · sub #${event.relatedSubscriptionId}` : '';
        const name = event.relatedUserName ? ` (${event.relatedUserName})` : '';
        return (
            <Tooltip title={`${out ? 'Restored on' : 'Restored here from'} ${who}${name} · ${details}`}>
                <Chip size="small" color="info" variant="outlined" clickable icon={EVENT_ICON[event.type]}
                    label={`${out ? 'Moved to' : 'Moved from'} ${who}${row}`}
                    component={RouterLink} to={`/users/${event.relatedUserId}?tab=premium`} target="_blank" rel="noopener" />
            </Tooltip>
        );
    }
    return (
        <Tooltip title={details}>
            <Chip size="small" variant="outlined" icon={EVENT_ICON[event.type] ?? <InfoOutlinedIcon fontSize="small" />} label={event.type} />
        </Tooltip>
    );
}

/** A plan's details: its products, grace, state and every benefit. */
function PlanDialog({ plan, options, onClose }: {
    plan: SubscriptionPlanAdminDto | null;
    options: EffectOptionDto[];
    onClose: () => void;
}) {
    if (plan == null) return null;
    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" gap={1} alignItems="center">
                    <WorkspacePremiumIcon color={plan.code === 'max' ? 'warning' : 'primary'} />
                    {plan.name}
                    <Chip size="small" label={plan.code} sx={{ fontFamily: 'monospace' }} />
                    {plan.active ? <Chip size="small" color="success" label="on sale" /> : <Chip size="small" label="inactive" />}
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack gap={2}>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                        {plan.products.map(p => (
                            <Chip key={p.productCode} icon={<PaymentsOutlinedIcon />}
                                label={`${p.productCode} · ${formatPrice(p.price, p.currency)} / ${p.periodDays} days`} />
                        ))}
                        <Chip icon={<HourglassBottomIcon />} label={`Grace ${plan.graceHours} h`} />
                        <Chip icon={<StarOutlineIcon />} label={`Rank ${plan.rank}`} />
                    </Stack>
                    <Typography variant="subtitle2">Benefits</Typography>
                    {plan.benefits.length === 0
                        ? <Empty text="None." />
                        : (
                            <Stack gap={1.25}>
                                {plan.benefits.map((b, i) => {
                                    const view = benefitView(b, options);
                                    return (
                                        <Stack key={i} direction="row" gap={1.5} alignItems="center">
                                            <Box sx={{ color: 'primary.main', display: 'flex' }}>{view.icon}</Box>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>{view.label}</Typography>
                                                <Typography variant="caption" color="text.secondary">{view.detail}</Typography>
                                            </Box>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        )}
                    <Stack direction="row" gap={1.5} alignItems="center">
                        <Box sx={{ color: 'primary.main', display: 'flex' }}><WorkspacePremiumIcon fontSize="small" /></Box>
                        <Box>
                            <Typography variant="body2" fontWeight={600}>{plan.tier === 'MAX' ? 'Max' : 'Plus'} badge in the app</Typography>
                            <Typography variant="caption" color="text.secondary">Comes with the plan's tier ({plan.tier}), not a benefit row</Typography>
                        </Box>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function GrantDialog({ open, plans, onClose, onGrant }: {
    open: boolean;
    plans: SubscriptionPlanAdminDto[];
    onClose: () => void;
    onGrant: (plan: string, days: number, note: string) => void;
}) {
    const [plan, setPlan] = React.useState('');
    const [days, setDays] = React.useState('30');
    const [note, setNote] = React.useState('');
    React.useEffect(() => {
        if (open) {
            setPlan(plans[0]?.code ?? '');
            setDays('30');
            setNote('');
        }
    }, [open, plans]);
    const dayCount = Number(days);
    const valid = plan !== '' && Number.isInteger(dayCount) && dayCount >= 1 && dayCount <= 365;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Grant a BETUP subscription</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Our own subscription (shop BETUP, a test row, event GRANTED, nothing charged): it gives the plan's benefits
                        until its grace end. A live BETUP subscription of the same plan is extended; one of another plan ends.
                    </Typography>
                    <TextField select label="Plan" value={plan} onChange={e => setPlan(e.target.value)} size="small">
                        {plans.map(p => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
                    </TextField>
                    <TextField label="Days" type="number" value={days} onChange={e => setDays(e.target.value)} size="small"
                        inputProps={{ min: 1, max: 365 }} error={!valid && days !== ''} helperText="1–365" />
                    <TextField label="Note (optional)" value={note} onChange={e => setNote(e.target.value)} size="small"
                        inputProps={{ maxLength: 200 }} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={!valid} onClick={() => onGrant(plan, dayCount, note)}>Grant</Button>
            </DialogActions>
        </Dialog>
    );
}

/** Gives a benefit offer template: its per-plan boosts, the days to take it, a note and the push. */
function GiveOfferDialog({ open, templates, plans, options, error, loading, onClose, onGive }: {
    open: boolean;
    templates: SubscriptionOfferOptionDto[];
    plans: SubscriptionPlanAdminDto[];
    options: EffectOptionDto[];
    error: string | null;
    loading: boolean;
    onClose: () => void;
    onGive: (template: SubscriptionOfferOptionDto, days: number, note: string, push: boolean) => void;
}) {
    const [templateId, setTemplateId] = React.useState<number | ''>('');
    const [days, setDays] = React.useState('');
    const [note, setNote] = React.useState('');
    const [push, setPush] = React.useState(true);
    const [submitted, setSubmitted] = React.useState(false);
    React.useEffect(() => {
        if (open) {
            const first = templates[0];
            setTemplateId(first?.id ?? '');
            setDays(first ? String(first.claimDays) : '');
            setNote('');
            setPush(true);
            setSubmitted(false);
        }
    }, [open, templates]);
    const template = templates.find(t => t.id === templateId) ?? null;
    const dayCount = Number(days);
    const valid = template != null && Number.isInteger(dayCount) && dayCount >= 1 && dayCount <= 90;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Give a subscription offer</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        The player takes it by subscribing (any plan) before it expires; then it boosts that subscription for as
                        long as it lasts. An upgrade carries it, a downgrade drops it.
                    </Typography>
                    <TextField select label="Template" value={templateId} size="small"
                        onChange={e => {
                            const next = templates.find(t => t.id === Number(e.target.value));
                            setTemplateId(next?.id ?? '');
                            if (next) setDays(String(next.claimDays));
                        }}>
                        {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name} · {t.claimDays} days to take</MenuItem>)}
                    </TextField>
                    {template && (
                        <Card variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>Boosts per plan</Typography>
                            <OfferBoosts benefits={template.benefits} plans={plans} options={options} />
                        </Card>
                    )}
                    <TextField label="Days to take it" type="number" value={days} onChange={e => setDays(e.target.value)} size="small"
                        inputProps={{ min: 1, max: 90 }} error={!valid && days !== ''}
                        helperText={`1–90${template ? ` · the template's ${template.claimDays}` : ''}`} />
                    <TextField label="Note (optional)" value={note} onChange={e => setNote(e.target.value)} size="small"
                        inputProps={{ maxLength: 200 }} />
                    <FormControlLabel control={<Checkbox checked={push} onChange={e => setPush(e.target.checked)} />}
                        label="Send push" />
                    {submitted && error && !loading && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={!valid || loading}
                    onClick={() => { if (template) { setSubmitted(true); onGive(template, dayCount, note.trim(), push); } }}>
                    Give offer
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function RevokeOfferDialog({ offer, error, loading, onClose, onRevoke }: {
    offer: UserSubscriptionOfferDto | null;
    error: string | null;
    loading: boolean;
    onClose: () => void;
    onRevoke: (note: string) => void;
}) {
    const [note, setNote] = React.useState('');
    const [submitted, setSubmitted] = React.useState(false);
    React.useEffect(() => { if (offer) { setNote(''); setSubmitted(false); } }, [offer]);
    return (
        <Dialog open={offer != null} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Revoke offer #{offer?.id}?</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    <Typography variant="body2">
                        {offer?.status === 'TAKEN'
                            ? `“${offer?.offerName ?? 'The offer'}” stops boosting subscription #${offer?.subscriptionId}: it keeps its plan's own benefits.`
                            : `“${offer?.offerName ?? 'The offer'}” can no longer be taken.`}
                    </Typography>
                    <TextField label="Reason (optional)" value={note} onChange={e => setNote(e.target.value)} size="small"
                        inputProps={{ maxLength: 200 }} />
                    {submitted && error && !loading && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button color="error" variant="contained" disabled={loading}
                    onClick={() => { setSubmitted(true); onRevoke(note.trim()); }}>Revoke</Button>
            </DialogActions>
        </Dialog>
    );
}

function RevokeDialog({ subscription, onClose, onRevoke }: {
    subscription: SubscriptionAdminDto | null;
    onClose: () => void;
    onRevoke: (note: string) => void;
}) {
    const [note, setNote] = React.useState('');
    React.useEffect(() => { if (subscription) setNote(''); }, [subscription]);
    return (
        <Dialog open={subscription != null} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Revoke subscription #{subscription?.id}?</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    <Typography variant="body2">
                        It ends now (REVOKED) and every effect it gave goes off — the player loses Ace right away; their conversations stay readable.
                    </Typography>
                    {subscription && subscription.shop !== 'BETUP' && (
                        <Alert severity="warning">
                            This is a {subscription.shop} subscription: revoking ends its benefits here only. The store keeps billing —
                            cancel or refund it in the store console.
                        </Alert>
                    )}
                    <TextField label="Reason (optional)" value={note} onChange={e => setNote(e.target.value)} size="small"
                        inputProps={{ maxLength: 200 }} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button color="error" variant="contained" onClick={() => onRevoke(note)}>Revoke</Button>
            </DialogActions>
        </Dialog>
    );
}

function EditEffectDialog({ effect, options, onClose, onSave }: {
    effect: SubscriptionEffectDto | null;
    options: { id: number; name: string | null; coef: number }[];
    onClose: () => void;
    onSave: (change: { effectId?: number; expires?: string; active?: boolean }) => void;
}) {
    const [effectId, setEffectId] = React.useState(0);
    const [expires, setExpires] = React.useState('');
    const [active, setActive] = React.useState(true);
    React.useEffect(() => {
        if (effect) {
            setEffectId(effect.effectId);
            setExpires(toLocalInput(effect.expires));
            setActive(effect.isActive);
        }
    }, [effect]);
    if (effect == null) return null;
    const change: { effectId?: number; expires?: string; active?: boolean } = {};
    if (effectId !== effect.effectId) change.effectId = effectId;
    if (expires !== '' && expires !== toLocalInput(effect.expires)) change.expires = new Date(expires).toISOString();
    if (active !== effect.isActive) change.active = active;
    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Edit effect #{effect.userEffectId}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    {effect.sourceType === 'SUBSCRIPTION' && (
                        <Alert severity="info">
                            Granted by subscription #{effect.sourceId}: a change here makes it differ from what the subscription gives, and
                            its validation will say so (Apply benefits puts it back).
                        </Alert>
                    )}
                    <TextField select label={`Effect (group ${effect.group})`} value={effectId} size="small"
                        onChange={e => setEffectId(Number(e.target.value))}>
                        {options.map(o => <MenuItem key={o.id} value={o.id}>{o.name ?? `Effect ${o.id}`} (coef {o.coef})</MenuItem>)}
                    </TextField>
                    <TextField label="Expires" type="datetime-local" value={expires} onChange={e => setExpires(e.target.value)}
                        size="small" InputLabelProps={{ shrink: true }} />
                    <FormControlLabel control={<Switch checked={active} onChange={e => setActive(e.target.checked)} />} label="Active" />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={Object.keys(change).length === 0} onClick={() => onSave(change)}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}
